/**
 * eventResources — every resource id a calendar event occupies.
 *
 * A dual event (aircraft + instructor) carries several ids in the DTO's
 * `resourceIds[]`, and their ORDER IS NOT STABLE: rebuilding the instructor
 * link (e.g. adding an instructor from the Weight & Balance screen) recreates
 * the event-resource rows and can put the instructor id FIRST. Anything that
 * reads `resourceIds[0]` (via the legacy-aliased `event.resourceID`) as "the"
 * resource therefore teleports the event between lanes/filters — the
 * "dragged event disappeared" bug of 2026-07-09 (it had jumped from the
 * aircraft row to the instructor row).
 *
 * Use this list for lane bucketing and resource-filter matching; treat
 * `event.resourceID` as nothing more than a legacy fallback.
 *
 * @module eventResources
 */

/**
 * @param {Object} event Normalized calendar event.
 * @returns {Array<string>} Unique resource ids as strings. Never empty for a
 *   filterable event: falls back to `[String(resourceID)]`, or `['null']`-free
 *   `[null]` when the event has no resource at all (keeps `matchesFilter`
 *   semantics identical to the old single-id check).
 */
export function eventResourceIdList(event) {
	const out = [];
	const push = v => {
		if (v == null) return;
		const s = String(v);
		if (!out.includes(s)) out.push(s);
	};
	if (Array.isArray(event?.resourceIds)) event.resourceIds.forEach(push);
	push(event?.resourceID);
	return out.length > 0 ? out : [null];
}

export default eventResourceIdList;
